import { Map, List } from 'immutable';
import { isDownloadableEsim, isInstalledEsim } from 'flow/utils/esim-utils';

export const getSimCardPUKCode = (simcard) => {
    return simcard.get('puk1');
};

export const getSimCardPINCode = (simcard) => {
    return simcard.get('pin1');
};

export const getSimCardImsi = (simcard) => {
    return simcard.get('imsi');
};

export const getSimCardIcc = (simcard) => {
    return simcard.get('icc');
};

export const isActiveSimcard = (simcard) => {
    return simcard.has('pin1') && simcard.has('puk1');
};

export const hasActiveMainSimCards = (mobileSubscription) => {
    const mainSimcards = getAllMainSimcards(mobileSubscription);
    return hasActiveSimcards(mainSimcards);
};

export const hasActiveTwinSimCards = (mobileSubscription) => {
    const twinSimcards = getAllTwinSimcards(mobileSubscription);
    return hasActiveSimcards(twinSimcards);
};

const hasActiveSimcards = (simcards) => {
    const activeSimcards = !simcards.filter(isActiveSimcard).isEmpty();
    return activeSimcards;
};

export const hasActiveEsimCards = (mobileSubscription) => {
    const hasActiveEsim =
        mobileSubscription &&
        !mobileSubscription
            .get('simcards', List())
            .filter((sim) => isInstalledEsim(sim.get('esimProfile')))
            .isEmpty();
    return hasActiveEsim;
};

export const getAllMainSimcards = (mobileSubscription) => {
    if (!mobileSubscription || mobileSubscription.isEmpty()) {
        return List();
    }
    const simCards = mobileSubscription.get('simcards');
    const mainSimcardCollection =
        simCards && simCards.find((sc) => sc.get('isMainSimcard'));
    const allMainSimcards =
        mainSimcardCollection && mainSimcardCollection.get('connectedSimCards');
    return allMainSimcards || List();
};

export const getAllTwinSimcards = (mobileSubscription) => {
    if (!mobileSubscription || mobileSubscription.isEmpty()) {
        return List();
    }
    const simCards = mobileSubscription.get('simcards');
    const twinSimcardCollection =
        simCards && simCards.find((sc) => !sc.get('isMainSimcard'));
    const allTwinSimcards =
        twinSimcardCollection && twinSimcardCollection.get('connectedSimCards');
    const allNonEmptyTwins =
        allTwinSimcards && allTwinSimcards.filter((sc) => !sc.isEmpty());
    return allNonEmptyTwins || List();
};

export const getSimcardInformation = (simcard, mobileSubscription) => {
    const simcardKey = simcard.get('key');
    const allSimcardInfos = mobileSubscription.get('simcards', Map());
    const simcardInfo = allSimcardInfos.reduce((red, simcardInfo) => {
        const allSimcards = simcardInfo.get('connectedSimCards', List());
        const simcard = allSimcards.find(
            (simcard) => simcard.get('key') === simcardKey,
        );
        if (simcard) {
            return (red = simcardInfo);
        } else {
            return red;
        }
    }, Map());
    return simcardInfo;
};

export const getSimCardStrings = (isEsim, isMainCard, isMoreThanOne) => {
    let blockTitleText, blockHelpText, confirmationText, reactivateHelpText;

    if (isEsim) {
        //NOTE! For now eSim is available in Sweden and Finland, and multiples only for Norway. So will leave those for now
        blockTitleText = 'settings.block_esim';
        blockHelpText = 'settings.block_esim_help_text';
        confirmationText = 'settings.block_esim_question';
        reactivateHelpText = 'settings.block_reactivate_esim_help_text';
    } else if (isMainCard) {
        blockTitleText = isMoreThanOne
            ? 'settings.block_simcard_multiples'
            : 'settings.block_simcard';
        blockHelpText = 'settings.block_simcard_help_text';
        confirmationText = isMoreThanOne
            ? 'settings.block_simcard_question_multiples'
            : 'settings.block_simcard_question';
        reactivateHelpText = isMoreThanOne
            ? 'settings.block_reactivate_simcard_help_text_multiples'
            : 'settings.block_reactivate_simcard_help_text';
    } else {
        blockTitleText = isMoreThanOne
            ? 'settings.block_twin_card_multiple'
            : 'settings.block_twin_card';
        blockHelpText = 'settings.block_twin_simcard_help_text';
        confirmationText = isMoreThanOne
            ? 'settings.block_twin_simcard_question_multiple'
            : 'settings.block_twin_simcard_question';
        reactivateHelpText = isMoreThanOne
            ? 'settings.block_reactivate_twin_simcard_help_text_multiples'
            : 'settings.block_reactivate_twin_simcard_help_text';
    }

    return {
        blockTitleText: blockTitleText,
        blockHelpText: blockHelpText,
        confirmationText: confirmationText,
        reactivateHelpText: reactivateHelpText,
    };
};

/**
 * eSIM is currently supported for:
 * - All Finnish mobile users.
 * - All Swedish mobile users that do not belong to a Cloud Partner/Wholesale customer. This is because we need
 * - All Belgian mobile users.
 *   customised SIM profiles for Cloud Partner/Wholesale customers, which are not yet in place.
 */
export const hasAccessToEsimSettings = (
    mobileSubscription,
    userHasMobileLicense,
    loggedInUserCountryCode,
) => {
    const hasActiveEsim =
        !mobileSubscription
            .get('simcards', List())
            .filter((sim) => isInstalledEsim(sim.get('esimProfile')))
            .isEmpty() ||
        !mobileSubscription
            .get('simcards', List())
            .filter((sim) => isDownloadableEsim(sim.get('esimProfile')))
            .isEmpty();

    return (
        userHasMobileLicense &&
        ((loggedInUserCountryCode === 'fi' && hasActiveEsim) ||
            ['se', 'be'].includes(loggedInUserCountryCode))
    );
};

export const sortSimcardByIMSI = (simcards) => {
    return simcards.sortBy((simcard) => simcard.get('imsi'));
};
